import type { StateCreator } from 'zustand'
import { toast } from 'sonner'
import { apiService } from '@/service/api'
import type {
  CreateSharesRequest,
  CreateSharesResponse,
  Share,
  ShareResourceType,
} from '@/types/shares'

/** Key for the per-resource share cache. */
const resourceKey = (type: ShareResourceType, id: string) => `${type}:${id}`

export interface SharesState {
  /** Live grants, keyed `${resource_type}:${resource_id}`. */
  sharesByResource: Record<string, Share[]>
  isSharesLoading: boolean
  fetchShares: (type: ShareResourceType, id: string) => Promise<void>
  createShares: (request: CreateSharesRequest) => Promise<CreateSharesResponse | null>
  revokeShare: (shareId: string, type: ShareResourceType, id: string) => Promise<void>
}

const messages = {
  error: {
    fetchShares: 'Error loading who has access',
    createShares: "Couldn't share. Please try again.",
    revokeShare: 'Error removing access',
  },
  success: {
    revokeShare: 'Access removed',
  },
}

/**
 * FS-663 share state.
 *
 * Deliberately holds only the DURABLE share list. Recipient-search results are
 * ephemeral and per-keystroke, so they stay in component state (the same call
 * the @-mention autocomplete makes) rather than accumulating in the store.
 */
export const sharesSlice: StateCreator<SharesState, [], [], SharesState> = (set, get) => ({
  sharesByResource: {},
  isSharesLoading: false,

  fetchShares: async (type, id) => {
    set({ isSharesLoading: true })
    try {
      const { shares } = await apiService.listShares(type, id)
      set({
        sharesByResource: { ...get().sharesByResource, [resourceKey(type, id)]: shares },
      })
    } catch {
      toast.error(messages.error.fetchShares)
      set({
        sharesByResource: { ...get().sharesByResource, [resourceKey(type, id)]: [] },
      })
    } finally {
      set({ isSharesLoading: false })
    }
  },

  createShares: async (request) => {
    try {
      const response = await apiService.createShares(request)
      // Refetch rather than splicing the response in: the server decides
      // pending-invite state and de-duplicates against existing live grants,
      // so its list is the truth and a local merge would drift.
      await get().fetchShares(request.resource_type, request.resource_id)
      return response
    } catch {
      toast.error(messages.error.createShares)
      return null
    }
  },

  revokeShare: async (shareId, type, id) => {
    try {
      await apiService.deleteShare(shareId)
      toast.success(messages.success.revokeShare)
      await get().fetchShares(type, id)
    } catch {
      toast.error(messages.error.revokeShare)
    }
  },
})
